import { useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { PageHeader, Panel } from "@/components/layout/AppShell";
import SignalCard, { FeaturedSignal } from "@/components/SignalCard";
import LiveChart from "@/components/LiveChart";
import ShareCardDialog from "@/components/ShareCardDialog";
import RsiExitPanel from "@/components/RsiExitPanel";
import { usePoll } from "@/context/AuthContext";
import { useMarket } from "@/context/MarketContext";
import { fmt } from "@/lib/format";

export const SEGMENTS = {
  "option-buyer": { eyebrow: "Options · Buy side", title: "Option Buyer", sub: "Directional CE / PE strikes with premium-based Entry, Stop Loss, Target and live Trailing SL status.", tf: 5 },
  "option-seller": { eyebrow: "Options · Write side", title: "Option Seller", sub: "Premium-collection setups with suggested strikes, hedge legs and premium-based exits.", tf: 5 },
  intraday: { eyebrow: "Equity · Cash", title: "Stock Intraday Trader", sub: "High-momentum intraday calls on liquid F&O stocks.", tf: 5 },
  futures: { eyebrow: "Derivatives", title: "Futures Trading", sub: "Spot vs Futures premium, OI build-up and directional futures signals.", futures: true, tf: 15 },
  commodity: { eyebrow: "MCX · NCDEX", title: "Multi-Commodity", sub: "Metals, energy and agricultural (Anaj) commodities with futures levels.", futures: true, tf: 15,
    tabs: [{ k: "all", l: "All" }, { k: "mcx", l: "Metals & Energy" }, { k: "agri", l: "Agri / Anaj" }] },
  investors: { eyebrow: "Positional", title: "Investors Window", sub: "Delivery accumulation picks and BTST (Buy Today Sell Tomorrow) opportunities.", tf: 15,
    tabs: [{ k: "all", l: "All" }, { k: "delivery", l: "Delivery" }, { k: "btst", l: "BTST" }, { k: "watch", l: "Watchlist" }] },
};

function Tabs({ tabs, value, onChange }) {
  return (
    <div className="inline-flex p-1 rounded-full bg-black/40 border border-white/10" data-testid="segment-tabs">
      {tabs.map((t) => (
        <button key={t.k} onClick={() => onChange(t.k)} data-testid={`segment-tab-${t.k}`}
          className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${value === t.k ? "bg-[#171C24] text-[#00FF66] shadow-[0_0_16px_rgba(0,255,102,.15)]" : "text-gray-400 hover:text-white"}`}>
          {t.l}
        </button>
      ))}
    </div>
  );
}

function LockedCards({ count }) {
  if (!count) return null;
  return (
    <div className="relative rx-card p-6 overflow-hidden min-h-[280px] flex flex-col items-center justify-center text-center sm:col-span-2 xl:col-span-1" data-testid="locked-signals">
      <div className="absolute inset-0 grid grid-cols-2 gap-3 p-5 blur-[6px] opacity-40 pointer-events-none" aria-hidden>
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="rounded-xl bg-gradient-to-br from-[#00FF66]/20 to-[#007AFF]/20" />)}
      </div>
      <div className="relative">
        <div className="mx-auto h-12 w-12 rounded-2xl border border-[#007AFF]/40 bg-[#007AFF]/10 grid place-items-center"><Lock size={18} className="text-[#007AFF]" /></div>
        <div className="font-display text-lg font-bold mt-4" data-testid="locked-count">{count} more signals locked</div>
        <p className="text-sm text-gray-400 mt-1">Free preview shows the 2 strongest. Basic unlocks every signal.</p>
        <Link to="/app/pricing" data-testid="unlock-signals-btn" className="inline-flex mt-5 rounded-full bg-[#007AFF] px-6 py-2.5 text-sm font-bold hover:shadow-[0_0_24px_rgba(0,122,255,.5)] transition-shadow">Unlock with Basic · ₹999/mo</Link>
      </div>
    </div>
  );
}

export default function SegmentPage() {
  const { segment } = useParams();
  const cfg = SEGMENTS[segment];
  const { data, error } = usePoll(cfg ? `/segment/${segment}` : null, 3000);
  const { quotes } = useMarket();
  const [tab, setTab] = useState("all");
  const [sel, setSel] = useState(null);
  const [share, setShare] = useState(null);
  const items = useMemo(() => (data?.items || []).filter((i) => tab === "all" || i.group === tab), [data, tab]);
  if (!cfg) return <Navigate to="/app" replace />;
  const symbol = sel && items.find((i) => i.symbol === sel) ? sel : items[0]?.symbol;
  const cur = items.find((i) => i.symbol === symbol);
  const q = quotes[symbol];

  return (
    <div data-testid={`segment-page-${segment}`}>
      <PageHeader eyebrow={cfg.eyebrow} title={cfg.title} sub={cfg.sub} right={cfg.tabs && <Tabs tabs={cfg.tabs} value={tab} onChange={setTab} />} />
      {error && <div className="text-sm text-[#FF3366] mb-4" data-testid="segment-error">{error}</div>}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-5 lg:gap-6 mb-5">
        <div className="xl:col-span-5">
          <FeaturedSignal key={segment} item={cur} segment={segment} onShare={setShare}
            liveScore={segment === "investors" ? undefined : q?.score} liveLtp={q?.ltp} />
        </div>
        <Panel className="xl:col-span-7" title={cur ? `${cur.symbol} · Live chart` : "Chart"} testId="segment-chart-panel"
          right={cur && (
            <div className="flex items-center gap-4 font-data text-xs text-gray-400">
              <span>Spot <b className="text-white">{fmt(q?.ltp ?? cur.ltp)}</b></span>
              <span className="hidden sm:inline">Fut <b className="text-white">{fmt(q?.fut ?? cur.fut)}</b></span>
              <span className="hidden md:inline">{cur.buildup}</span>
            </div>
          )}>
          {symbol ? <LiveChart symbol={symbol} height={430} defaultTf={cfg.tf} /> : <div className="h-[430px] grid place-items-center text-gray-500 text-sm">No instruments in this filter</div>}
        </Panel>
      </div>
      {(segment === "option-buyer" || segment === "option-seller") && symbol && (
        <div className="mb-8"><RsiExitPanel segment={segment} symbol={symbol} /></div>
      )}
      <div className="flex items-center justify-between mb-4">
        <h2 className="rx-label">All signals · tap a card to focus</h2>
        {data && <span className="font-data text-[11px] text-gray-500">{items.length} shown</span>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="signal-grid">
        {!data && Array.from({ length: 6 }).map((_, i) => <div key={i} className="rx-card h-80 animate-pulse" />)}
        {items.map((it) => (
          <SignalCard key={it.symbol} item={it} selected={it.symbol === symbol} onSelect={setSel} onShare={setShare} showFutures={cfg.futures} />
        ))}
        <LockedCards count={data?.locked_count} />
      </div>
      <ShareCardDialog item={share} onClose={() => setShare(null)} />
    </div>
  );
}
